import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from '../../../utils/dateUtils';

const EventHeader = ({ event, statusInfo, typeInfo }) => {
  const { t } = useTranslation();
  const [elapsedTime, setElapsedTime] = useState('');

  // Check if this is a match with teams
  const isMatch = event.eventType === 'Match' && event.teamInfo && event.teamInfo.team1 && event.teamInfo.team2;
  
  // Get team information for single-team events
  const singleTeam = event.team || (event.eventType !== 'Match' && event.teamInfo);

  // Timer for in-progress events
  useEffect(() => {
    if (event?.eventStatus === 'In Progress' && event?.startDate) {
      const updateTimer = () => {
        const startTime = new Date(event.startDate);
        const now = new Date();
        const diff = now - startTime;

        if (diff > 0) {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);

          if (hours > 0) {
            setElapsedTime(`${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
          } else {
            setElapsedTime(`${minutes}:${seconds.toString().padStart(2, '0')}`);
          }
        } else {
          setElapsedTime('0:00');
        }
      };

      // Update immediately
      updateTimer();

      // Update every second
      const interval = setInterval(updateTimer, 1000);

      return () => clearInterval(interval);
    }
  }, [event?.eventStatus, event?.startDate]);

  return (
    <div className="bg-gradient-to-br from-white via-blue-50 to-purple-50 rounded-2xl shadow-2xl border border-gray-200 overflow-hidden mb-8">
      {/* Hero Section */}
      <div className="relative p-8">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5"></div>
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        ></div>
        
        <div className="relative z-10">
          {/* Match Header with Team Logos */}
          {isMatch ? (
            <div className="text-center mb-8">
              {/* Teams Display */}
              <div className="flex items-center justify-center space-x-8 mb-6">
                {/* Team 1 */}
                <div className="flex flex-col items-center space-y-3">
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-white shadow-2xl border-4 border-blue-100">
                    {event.teamInfo?.team1?.logo ? (
                      <img
                        src={event.teamInfo.team1.logo}
                        alt={event.teamInfo.team1?.name || 'Team 1'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 515.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 919.288 0M15 7a3 3 0 11-6 0 3 3 0 616 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      {event.teamInfo?.team1?.name || 'Team 1'}
                    </h3>
                    <p className="text-sm text-gray-600 capitalize">
                      {event.teamInfo?.team1?.sport || 'Sport'} • {event.teamInfo?.team1?.type || 'Team'}
                    </p>
                  </div>
                  {/* Score if available */}
                  {event.metaData?.score?.team1 !== undefined && (
                    <div className="text-4xl font-bold text-blue-600 bg-blue-100 rounded-lg px-4 py-2 shadow-lg">
                      {event.metaData.score.team1}
                    </div>
                  )}
                </div>
                
                {/* VS Divider with Timer for In Progress */}
                <div className="flex flex-col items-center space-y-4">
                  <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full p-6 shadow-xl border-4 border-white">
                    <span className="text-2xl font-bold text-white">VS</span>
                  </div>
                  
                  {/* Timer for In Progress events */}
                  {event?.eventStatus === 'In Progress' && elapsedTime && (
                    <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg px-4 py-2 shadow-lg border-2 border-white">
                      <div className="flex items-center space-x-2">
                        <svg className="w-4 h-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-mono text-lg font-bold">{elapsedTime}</span>
                      </div>
                      <div className="text-xs text-center opacity-90">{t("player.elapsed")}</div>
                    </div>
                  )}
                  
                  {/* Regular time display for other states */}
                  {event?.eventStatus !== 'In Progress' && (
                    <div className="text-sm text-gray-700 text-center font-medium bg-white rounded-lg px-3 py-1 shadow-md">
                      {format(new Date(event.startDate), 'MMM dd, HH:mm')}
                    </div>
                  )}
                </div>
                
                {/* Team 2 */}
                <div className="flex flex-col items-center space-y-3">
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-white shadow-2xl border-4 border-green-100">
                    {event.teamInfo?.team2?.logo ? (
                      <img
                        src={event.teamInfo.team2.logo}
                        alt={event.teamInfo.team2?.name || 'Team 2'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 515.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 919.288 0M15 7a3 3 0 11-6 0 3 3 0 616 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      {event.teamInfo?.team2?.name || 'Team 2'}
                    </h3>
                    <p className="text-sm text-gray-600 capitalize">
                      {event.teamInfo?.team2?.sport || 'Sport'} • {event.teamInfo?.team2?.type || 'Team'}
                    </p>
                  </div>
                  {/* Score if available */}
                  {event.metaData?.score?.team2 !== undefined && (
                    <div className="text-4xl font-bold text-green-600 bg-green-100 rounded-lg px-4 py-2 shadow-lg">
                      {event.metaData.score.team2}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Event Title for Matches */}
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {event.name}
              </h1>
            </div>
          ) : (
            /* Non-Match Event Header */
            <div className="text-center mb-8">
              {/* Single Team Logo for Training/Events */}
              {singleTeam && (
                <div className="flex flex-col items-center space-y-4 mb-6">
                  <div className="w-32 h-32 rounded-full overflow-hidden bg-white shadow-2xl border-4 border-purple-100">
                    {singleTeam.logo ? (
                      <img
                        src={singleTeam.logo}
                        alt={singleTeam.name || 'Team'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                        <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 515.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 919.288 0M15 7a3 3 0 11-6 0 3 3 0 616 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {singleTeam.name || 'Team'}
                    </h3>
                    <div className="flex items-center justify-center space-x-3">
                      <span className="text-sm px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
                        {singleTeam.sport || 'Sport'}
                      </span>
                      {singleTeam.type && (
                        <span className="text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                          {singleTeam.type}
                        </span>
                      )}
                      {singleTeam.gender && (
                        <span className="text-sm px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium capitalize">
                          {singleTeam.gender}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex items-center justify-center space-x-6 mb-6">
                <div className={`${typeInfo.bgColor} ${typeInfo.color} p-6 rounded-2xl shadow-lg border-2 border-white`}>
                  {typeInfo.icon}
                </div>
              </div>
              
              <h1 className="text-4xl font-bold text-gray-900 mb-3">
                {event.name}
              </h1>
              <div className="text-xl text-gray-600 font-medium">
                {event.eventType}
                {event.participantsCount && (
                  <span className="mx-2">•</span>
                )}
                {event.participantsCount && (
                  <span>{event.participantsCount} {t("common.participants")}</span>
                )}
              </div>
            </div>
          )}

          {/* Status Info with Timer for non-match events */}
          <div className="flex items-center justify-center space-x-6 mb-8">
            <div className={`inline-flex items-center px-6 py-3 rounded-full text-sm font-semibold border-2 border-white shadow-lg ${statusInfo.bgColor} ${statusInfo.textColor}`}>
              <div className={`w-3 h-3 rounded-full mr-3 ${statusInfo.dotColor} animate-pulse`}></div>
              {statusInfo.status}
            </div>
            
            {/* Timer for In Progress non-match events */}
            {!isMatch && event?.eventStatus === 'In Progress' && elapsedTime && (
              <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full px-6 py-3 shadow-lg border-2 border-white">
                <div className="flex items-center space-x-2">
                  <svg className="w-4 h-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-mono text-lg font-bold">{elapsedTime}</span>
                </div>
              </div>
            )}
            
            <div className="inline-flex items-center px-6 py-3 rounded-full text-sm font-semibold bg-white text-gray-700 border-2 border-gray-200 shadow-lg">
              <div className={`w-6 h-6 mr-3 ${typeInfo.color}`}>
                {typeInfo.icon && React.cloneElement(typeInfo.icon, { className: 'w-4 h-4' })}
              </div>
              {event.eventType}
            </div>
          </div>

          {/* Description */}
          {event.description && (
            <div className="text-center mb-8">
              <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto bg-white/70 rounded-xl p-6 shadow-lg">
                {event.description}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Details Section */}
      <div className="bg-gradient-to-r from-gray-50 to-blue-50 border-t border-gray-200">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Date and Time */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                {t("player.dateAndTime")}
              </h4>
              <p className="text-gray-700 text-lg">
                {format(new Date(event.startDate), 'MMM dd, yyyy • HH:mm')}
                {event.endDate && (
                  <span> - {format(new Date(event.endDate), 'HH:mm')}</span>
                )}
              </p>
              {event.isToday && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t("player.today")}
                  </span>
                </div>
              )}
              
              {/* Live Timer Display for In Progress events */}
              {event?.eventStatus === 'In Progress' && elapsedTime && (
                <div className="mt-3 p-3 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">{t("player.elapsedTime")}:</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="font-mono text-lg font-bold text-orange-600">{elapsedTime}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Location */}
            {event.metaData?.location && (
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 616 0z" />
                    </svg>
                  </div>
                  {t("common.location")}
                </h4>
                <p className="text-gray-700 text-lg">{event.metaData.location}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventHeader; 